import React from 'react';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

interface Props {
  name: string;
  options?: string[];
  label?: string;
  isRequired?: boolean;
  isFullWidth?: boolean;
  defaultValue?: string;
  isError?: boolean;
  sx?: SxProps;
  variant?: 'standard' | 'outlined' | 'filled';
  showClearButton?: boolean;
}

export function AppControlledSelect({
  label = '',
  name,
  isRequired = false,
  isFullWidth = true,
  isError = false,
  options,
  defaultValue,
  sx: styles,
  variant,
  showClearButton = false,
}: Props): JSX.Element {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: isRequired }}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <FormControl
          fullWidth={isFullWidth}
          error={isError}
          variant={variant}
          sx={styles}
        >
          <InputLabel id={name}>{isRequired ? label + ' *' : label}</InputLabel>

          <Select
            labelId={name}
            onChange={(event) => {
              setValue(name, event.target.value);
              onChange(event.target.value);
            }}
            label={isRequired ? label + ' *' : label}
            value={value || ''}
            MenuProps={{
              style: {
                maxHeight: 200,
              },
            }}
            endAdornment={
              <>
                {showClearButton && !!value && (
                  <IconButton
                    sx={{ mr: 1 }}
                    size="small"
                    onClick={() => onChange('')}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                )}
              </>
            }
          >
            {options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>

          {isError && <FormHelperText>This is required</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
